import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Briefcase, CheckCircle2, ArrowRight, ArrowLeft,
  Target, Users, Calendar, FileText, AlertTriangle, TrendingUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const phases = [
  {
    title: 'Phase 1: Initiation',
    icon: Target,
    description: 'Define the project and get authorization to proceed.',
    tasks: [
      { task: 'Define the project purpose and objectives', detail: 'Clearly articulate what the project will accomplish and why it matters to the organization.' },
      { task: 'Identify key stakeholders', detail: 'List everyone who has an interest in or will be affected by the project outcomes.' },
      { task: 'Create a project charter', detail: 'Document the project\'s purpose, objectives, scope, and key stakeholders in a formal authorization document.' },
      { task: 'Conduct a feasibility assessment', detail: 'Evaluate whether the project is technically, financially, and operationally viable.' },
      { task: 'Get formal project approval', detail: 'Obtain sign-off from the project sponsor and key decision-makers to proceed.' },
    ]
  },
  {
    title: 'Phase 2: Planning',
    icon: Calendar,
    description: 'Develop a roadmap for how the project will be executed.',
    tasks: [
      { task: 'Define the scope in detail', detail: 'Document exactly what is included and excluded from the project to prevent scope creep.' },
      { task: 'Create a Work Breakdown Structure (WBS)', detail: 'Break the project into smaller, manageable tasks and deliverables.' },
      { task: 'Develop a project schedule', detail: 'Sequence tasks, estimate durations, and create a timeline with milestones.' },
      { task: 'Estimate and allocate budget', detail: 'Calculate costs for resources, materials, and contingencies.' },
      { task: 'Identify and plan for risks', detail: 'List potential risks, assess their impact and probability, and develop mitigation strategies.' },
      { task: 'Define quality standards', detail: 'Establish criteria for measuring project success and deliverable quality.' },
      { task: 'Create a communication plan', detail: 'Define who needs what information, when, and through what channels.' },
      { task: 'Assemble the project team', detail: 'Identify required skills and assign team members to roles and responsibilities.' },
    ]
  },
  {
    title: 'Phase 3: Execution',
    icon: Users,
    description: 'Put the plan into action and manage the work.',
    tasks: [
      { task: 'Kick off the project', detail: 'Hold a launch meeting to align the team on objectives, roles, and expectations.' },
      { task: 'Direct and manage project work', detail: 'Lead the team in completing tasks according to the project plan.' },
      { task: 'Manage team dynamics', detail: 'Foster collaboration, resolve conflicts, and keep morale high.' },
      { task: 'Implement quality assurance', detail: 'Ensure work meets defined quality standards through reviews and testing.' },
      { task: 'Manage stakeholder expectations', detail: 'Keep stakeholders informed and engaged throughout the project.' },
      { task: 'Conduct regular status meetings', detail: 'Hold consistent check-ins to review progress, address issues, and adjust plans.' },
      { task: 'Document decisions and changes', detail: 'Keep a record of all significant decisions and approved changes.' },
    ]
  },
  {
    title: 'Phase 4: Monitoring & Controlling',
    icon: TrendingUp,
    description: 'Track progress and make adjustments as needed.',
    tasks: [
      { task: 'Track progress against baseline', detail: 'Compare actual performance to planned schedule, budget, and scope.' },
      { task: 'Manage scope changes', detail: 'Evaluate change requests, assess impact, and follow a formal change control process.' },
      { task: 'Monitor risks', detail: 'Continuously assess risks and implement mitigation plans as needed.' },
      { task: 'Control costs', detail: 'Track spending, forecast final costs, and take corrective action if over budget.' },
      { task: 'Measure quality', detail: 'Verify deliverables meet requirements and standards.' },
      { task: 'Report status to stakeholders', detail: 'Provide regular updates on progress, issues, and forecasts.' },
      { task: 'Manage issues', detail: 'Log issues, assign owners, track resolution, and escalate when necessary.' },
    ]
  },
  {
    title: 'Phase 5: Closing',
    icon: FileText,
    description: 'Formally complete and hand off the project.',
    tasks: [
      { task: 'Verify all deliverables are complete', detail: 'Confirm all project outputs meet acceptance criteria.' },
      { task: 'Obtain formal acceptance', detail: 'Get sign-off from the project sponsor and key stakeholders.' },
      { task: 'Conduct a lessons learned session', detail: 'Document what went well, what didn\'t, and recommendations for future projects.' },
      { task: 'Archive project documents', detail: 'Store all project documentation in an accessible location for future reference.' },
      { task: 'Release project resources', detail: 'Transition team members back to their functional roles or to new projects.' },
      { task: 'Celebrate success', detail: 'Recognize team contributions and celebrate project completion.' },
      { task: 'Close contracts', detail: 'Ensure all vendor and contractor obligations are fulfilled and payments made.' },
    ]
  },
];

const pmTools = [
  { name: 'Gantt Charts', use: 'Visualize project schedule and task dependencies' },
  { name: 'Kanban Boards', use: 'Track work items through stages (To Do, In Progress, Done)' },
  { name: 'RACI Matrix', use: 'Clarify roles: Responsible, Accountable, Consulted, Informed' },
  { name: 'Risk Register', use: 'Document and track identified risks and mitigation plans' },
  { name: 'Status Reports', use: 'Communicate progress, issues, and forecasts to stakeholders' },
  { name: 'Change Request Log', use: 'Track and manage requested changes to scope, schedule, or budget' },
];

const commonMistakes = [
  { mistake: 'Unclear objectives', fix: 'Define SMART goals (Specific, Measurable, Achievable, Relevant, Time-bound)' },
  { mistake: 'Poor scope definition', fix: 'Document detailed requirements and get stakeholder sign-off before starting' },
  { mistake: 'Unrealistic schedules', fix: 'Build in buffers, involve the team in estimates, and plan for the unexpected' },
  { mistake: 'Inadequate communication', fix: 'Over-communicate early and often; silence breeds assumptions' },
  { mistake: 'Ignoring risks', fix: 'Identify risks early and revisit them regularly throughout the project' },
  { mistake: 'Scope creep', fix: 'Use a formal change control process for all scope changes' },
];

const GuideProjectManagementPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guide-project-management-page">
      <SEO 
        title="Project Management Guide - From Initiation to Closing"
        description="A comprehensive guide to project management fundamentals. Learn the five phases of project management, essential tools, and common pitfalls to avoid."
        keywords="project management guide, PMP basics, project planning, project execution, project management phases, WBS, Gantt chart, project manager tips"
        url="/resources/guides/project-management"
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-indigo-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <Link to="/resources/guides" className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 mb-6 transition-colors">
            <ArrowLeft className="h-4 w-4" />
            Back to Guides
          </Link>
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 rounded-full mb-6">
            <Briefcase className="h-4 w-4 text-indigo-600" />
            <span className="text-sm font-medium text-indigo-700">Project Management</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-carbon-black mb-4" data-testid="guide-title">
            Project Management Guide
          </h1>
          <p className="text-xl text-slate-gray leading-relaxed">
            A comprehensive guide to managing projects from initiation to closing. Whether you're a new project manager or looking to refresh your skills, this guide covers the fundamentals that drive project success.
          </p>
        </div>
      </section>

      {/* Why PM Matters */}
      <section className="py-12 bg-white border-b border-gray-100">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl font-bold text-carbon-black mb-6">Why Project Management Matters</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            <div className="text-center p-6 bg-indigo-50 rounded-xl">
              <div className="text-3xl font-bold text-indigo-600 mb-2">70%</div>
              <p className="text-sm text-slate-gray">of projects fail to meet their original goals</p>
            </div>
            <div className="text-center p-6 bg-indigo-50 rounded-xl">
              <div className="text-3xl font-bold text-indigo-600 mb-2">2.5x</div>
              <p className="text-sm text-slate-gray">more likely to succeed with formal PM practices</p>
            </div>
            <div className="text-center p-6 bg-indigo-50 rounded-xl">
              <div className="text-3xl font-bold text-indigo-600 mb-2">28%</div>
              <p className="text-sm text-slate-gray">of budget typically wasted due to poor PM</p>
            </div>
          </div>
          <p className="text-slate-gray mt-8 leading-relaxed">
            Good project management isn't about following rigid processes—it's about having a framework that helps you deliver results consistently. The principles in this guide apply whether you're managing a small internal initiative or a complex multi-team effort.
          </p>
        </div>
      </section>

      {/* Phases */}
      {phases.map((phase, phaseIdx) => {
        const IconComponent = phase.icon;
        return (
          <section key={phaseIdx} className={`py-12 sm:py-16 ${phaseIdx % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-indigo-500 to-blue-500 flex items-center justify-center flex-shrink-0">
                  <IconComponent className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black">{phase.title}</h2>
                  <p className="text-slate-gray mt-1">{phase.description}</p>
                </div>
              </div>
              
              <div className="space-y-4 ml-16">
                {phase.tasks.map((item, itemIdx) => (
                  <div key={itemIdx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-start gap-3">
                      <CheckCircle2 className="h-5 w-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-carbon-black mb-1">{item.task}</h3>
                        <p className="text-sm text-slate-gray">{item.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        );
      })}

      {/* Essential Tools */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">Essential PM Tools</h2>
          
          <div className="grid sm:grid-cols-2 gap-4">
            {pmTools.map((tool, idx) => (
              <div key={idx} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm">
                <h3 className="font-bold text-indigo-600 mb-2">{tool.name}</h3>
                <p className="text-sm text-slate-gray">{tool.use}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-8 text-center">Common Mistakes to Avoid</h2>
          
          <div className="space-y-4">
            {commonMistakes.map((item, idx) => (
              <div key={idx} className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <div className="flex items-start gap-3 mb-2">
                  <AlertTriangle className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <span className="font-semibold text-carbon-black">{item.mistake}</span>
                </div>
                <div className="flex items-start gap-3 ml-8">
                  <CheckCircle2 className="h-4 w-4 text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-slate-gray">{item.fix}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-indigo-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Need Help With Your Project?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Our team includes certified PMP professionals who can help you plan and execute projects successfully.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/resources/consultation">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Book a Consultation
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/resources/guides">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-indigo-400 text-indigo-600 hover:bg-indigo-50 transition-all w-full sm:w-auto">
                More Guides
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuideProjectManagementPage;
